import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {PathService} from "./path.service";

enum LogLevel {
    LOG,
    WARN,
    ERROR
}

/** A service that knows not to log anything if running on the live site, log to console otherwise, and also in that
 * case notify any registered observers of what is logged, such as for showing/logging the info elsewhere.
 */
@Injectable({
    providedIn: 'root'
})
export class LogService {
    /** Used to notify interested observers of logged strings */
    private subject: Subject<string> = new Subject<string>();

    /** Whether we're running on the live site */
    private readonly isLive: boolean;

    constructor(private pathService: PathService) {
        this.isLive = this.pathService.isLiveSite();
    }

    private logByLevel(level:LogLevel, ...values: any[]) {
        // Only allow logging if not on live site or if logging an error
        if (!this.isLive || level == LogLevel.ERROR) {
            let out: string = values.map((v: any) => LogService.DeepString(v)).join(' ');
            // Use something like this if you want to log live objects to console, like Chrome does
            // console.log(values[0], ...values.slice(1));
            switch (level) {
                case LogLevel.LOG:
                    this.subject.next(out);
                    console.log(out);
                    break;
                case LogLevel.WARN:
                    this.subject.next(`[!] ${out}`);
                    console.warn(out);
                    break;
                case LogLevel.ERROR:
                    this.subject.next(`[!!] ${out}`);
                    console.error(out);
                    break;
            }
        }
    }

    log(...values: any[]) {
        this.logByLevel(LogLevel.LOG, ...values);
    }

    warn(...values: any[]) {
        this.logByLevel(LogLevel.WARN, ...values);
    }

    error(...values: any[]) {
        this.logByLevel(LogLevel.ERROR, ...values);
    }

    /** Provides the observable from which an interested party (e.g. a visual logging component) can be notified about logged strings
     * as they happen
     *
     * @returns {Observable<string>}
     */
    asObservable(): Observable<string> {
        return this.subject.asObservable();
    }

    /** Converts a given variable to a string and returns it, by recursively going through its items (arrays) or
     * properties (objects), and formatting the results accordingly, into a single-line string.
     *
     * @param v the variable to be converted to string
     * @param level {number} recursion level (default: 0)
     * @returns {string} the resulting output string
     */
    private static DeepString(v:any, level:number=0): string {
        if (level >= 3)
            return "...";
        let out:string;
        if (v && typeof v === "object") {
            if (v.constructor === Array) {
                out = '[' + v.map((x: any) => LogService.DeepString(x, level+1)).join(', ') + ']';
            } else {
                out = '{' + Object.keys(v).map(key => key + ": " + LogService.DeepString(v[key], level+1)).join(', ') + '}';
            }
        } else if (v === null) {
            out = "null";
        } else if (v === undefined) {
            out = "undefined";
        } else if (typeof v === "function") {
            out = "() => {...}";
        } else
            out = v.toString();
        return out;
    }

}

